/**
 * Live lessons callables (functions/src/liveLessons). The rules live on the
 * server; these wrappers only carry arguments in and results out.
 */
import { collection, doc, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import type { LiveChapter } from '../types/liveCall';
import { leadMinutesOf } from '../utils/bookingWindow';

// ── Offers ────────────────────────────────────────────────────────────────────

export interface LiveOfferSummary {
  /** Live lessons on for this coach (setLiveOffers), with an offer to book. */
  liveEnabled: boolean;
  introOfferingId: string | null;
  singleOfferingId: string | null;
  singlePriceCents: number;
  membershipOfferingId: string | null;
  membershipPriceCents: number;
  /** Percent off four Live Video Lessons. */
  membershipDiscountPercent: number;
  /** Null when the coach chose no limit: then nothing is shown. 10 when never set. */
  spotsLeft: number | null;
}

const _getLiveOfferSummary = httpsCallable<{ coachId: string }, LiveOfferSummary>(
  functions,
  'getLiveOfferSummary',
);
export async function getLiveOfferSummary(coachId: string): Promise<LiveOfferSummary> {
  const { data } = await _getLiveOfferSummary({ coachId });
  return data;
}

/** getLiveBookingContext (docs/live-lessons-data.md section 6). */
export interface LiveBookingContext {
  coachId: string;
  playerId: string;
  intro: { eligible: boolean; reason: 'own_profile' | 'not_offered' | 'already_had' | null; message: string | null };
  membership: null | {
    membershipId: string;
    status: string;
    creditsAvailable: number;
    canBook: boolean;
    cannotBookReason: 'not_active' | 'payment_issue' | 'no_credits' | null;
    refund: { open: boolean; reason: string | null; completedLessons: number; closesAt?: string | null;
      /** After the second lesson's call opens a refund is a support request (requestLiveRefund). */
      via?: 'automatic' | 'support'; requestedAt?: string | null };
    /** True from purchase until the second lesson's call opens, then false for good (the weekly time contract, a7f0e1f9). */
    refundAvailable?: boolean;
    nextCharge: { status: string; at: string | null; amountCents: number | null; latestAt?: string; endsAt?: string };
    paymentIssue: { since: string; message: string } | null;
    /** The weekly time contract: the lessons it holds, booked or held, a held one with its mark. */
    weeklyTime?: {
      weekday: number; time: string; lessonMode: 'match_review' | 'guidance_call'; line: string;
      upcoming: Array<{ startAt: string; state: 'booked' | 'held'; holdId?: string;
        /** "Possible conflict · John has time off that day", or null. */
        conflict?: string | null }>;
    } | null;
    /** v2.0: this month's lessons booked, of the month's, and lessons moved in from earlier months. */
    thisMonth?: { booked: number; of: number; fromEarlierMonths: Array<{ bookingId: string; scheduledStartAt: string; label: string }> };
  };
  timeZone: { zone: string; confirmed: boolean; confirmedAt: string | null };
  paymentFailed: boolean;
  bookingWindow: {
    earliestStartAt: string; latestStartAt: string; minHoursBefore: number; maxDaysAhead: number;
    /** The booking lead time in minutes (LIVE_MIN_LEAD_MINUTES on the server). Absent from older servers. */
    minMinutesBefore?: number;
    /** The open-time grid in minutes (LIVE_SLOT_MINUTES), once the server reports it. */
    slotMinutes?: number;
  };
  /** Asked with a bookingId (the room): that lesson's kind, and what its coach may recommend in it. */
  lesson?: { bookingId: string; offeringType: string | null; recommendOffers: Array<'single' | 'membership' | 'program'> };
}

const _getLiveBookingContext = httpsCallable<{ coachId: string; playerId?: string; bookingId?: string }, LiveBookingContext>(
  functions,
  'getLiveBookingContext',
);
export async function getLiveBookingContext(coachId: string,
  o: { playerId?: string; bookingId?: string } = {}): Promise<LiveBookingContext> {
  const { data } = await _getLiveBookingContext({ coachId, ...o });
  leads.set(coachId, { at: Date.now(), minutes: Promise.resolve(leadMinutesOf(data.bookingWindow)) });
  return data;
}

/** The server's lead time per coach, kept five minutes; every context read refreshes it. */
const leads = new Map<string, { at: number; minutes: Promise<number> }>();
const LEAD_KEEP_MS = 5 * 60_000;

/**
 * The booking lead time in minutes, from getLiveBookingContext's
 * bookingWindow.minMinutesBefore; 120 when the field is missing or the
 * read fails.
 */
export function getBookingLeadMinutes(coachId: string): Promise<number> {
  const kept = leads.get(coachId);
  if (kept && Date.now() - kept.at < LEAD_KEEP_MS) return kept.minutes;
  const minutes = _getLiveBookingContext({ coachId })
    .then(({ data }) => leadMinutesOf(data.bookingWindow))
    .catch(() => leadMinutesOf(null));
  leads.set(coachId, { at: Date.now(), minutes });
  return minutes;
}

/** attachLiveMatch: the match for a booked Match Review, sent after booking. */
const _attachLiveMatch = httpsCallable<
  { bookingId: string; playerVideoUrl: string; playerVideoStoragePath: string },
  { uploadId: string }
>(functions, 'attachLiveMatch');
export async function attachLiveMatch(
  bookingId: string, video: { url: string; storagePath: string },
): Promise<{ uploadId: string }> {
  const { data } = await _attachLiveMatch({
    bookingId, playerVideoUrl: video.url, playerVideoStoragePath: video.storagePath,
  });
  return data;
}

const _setLiveMembershipCap = httpsCallable<{ cap: number | null }, { cap: number | null }>(
  functions,
  'setLiveMembershipCap',
);
/** A whole number from 0 to 500, or null to clear the cap. */
export async function setLiveMembershipCap(cap: number | null): Promise<number | null> {
  const { data } = await _setLiveMembershipCap({ cap });
  return data.cap;
}

// ── Moves (v2.0 in services/liveV2) and cancelling ────────────────────────────

const call = <I, O>(name: string) => {
  const fn = httpsCallable<I, O>(functions, name);
  return async (input: I): Promise<O> => (await fn(input)).data;
};

export const cancelLiveLesson =
  call<{ bookingId: string }, { status: string; refunded: boolean }>('cancelLiveCallBooking');

/**
 * setLiveLessonMode: the player turns a booked lesson into a Match Review or
 * a Guidance Call, until it starts (the server refuses from the start on).
 * Deployed with live-lessons 5ea6ac1; the result also says whether the mode
 * changed. SET_LIVE_LESSON_MODE_LIVE turns the stub off.
 */
const SET_LIVE_LESSON_MODE_LIVE = true;
type LessonModeKey = 'match_review' | 'guidance_call';
const _setLiveLessonMode = call<{ bookingId: string; mode: LessonModeKey }, { lessonMode: LessonModeKey; changed?: boolean }>('setLiveLessonMode');
export async function setLiveLessonMode(input: { bookingId: string; mode: LessonModeKey }): Promise<{ lessonMode: LessonModeKey }> {
  if (!SET_LIVE_LESSON_MODE_LIVE) return { lessonMode: input.mode };
  return _setLiveLessonMode(input);
}

/**
 * The sentence the server gave when it refused (a time just taken, a
 * request too close to the start), or a generic one when it failed.
 */
export function liveLessonsErrorMessage(err: unknown, fallback: string): string {
  const code = (err as { code?: string }).code ?? '';
  const stated = err instanceof Error && code.startsWith('functions/') &&
    code !== 'functions/internal' && code !== 'functions/unknown';
  return stated ? (err as Error).message : fallback;
}

// ── Recommend (spec 3) ────────────────────────────────────────────────────────

export type RecommendOffer = 'single' | 'membership' | 'program';

/** One offer the coach sent in a call, as the player's sheet shows it. */
export interface LiveRecommendation {
  offer: RecommendOffer;
  title: string;
  note: string;
  coachId: string;
  playerId: string;
  offeringId: string | null;
  priceCents: number;
  copyLines: string[];
  status: 'sent' | 'booked';
  sentAt: string;
}

/** The call's recommendations, read on the caller's side of the rule. */
export function subscribeRecommendations(
  bookingId: string,
  uid: string,
  role: 'coach' | 'player',
  onChange: (recs: LiveRecommendation[]) => void,
): () => void {
  const q = query(
    collection(db, 'liveCallBookings', bookingId, 'recommendations'),
    where(role === 'coach' ? 'coachId' : 'playerId', '==', uid),
  );
  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.map((d) => d.data() as LiveRecommendation)),
    () => onChange([]),
  );
}

export const sendLiveRecommendation =
  call<{ bookingId: string; offer: RecommendOffer; note?: string },
    { offer: RecommendOffer; priceCents: number; copyLines: string[] }>('sendLiveRecommendation');
export const purchaseLiveRecommendedSingle =
  call<{ bookingId: string; paymentMethodId?: string },
    { status: 'booked' | 'requires_payment'; reservationId: string; clientSecret?: string | null }>('purchaseLiveRecommendedSingle');
export const confirmLiveRecommendedSingle =
  call<{ reservationId: string }, { status: 'booked' }>('confirmLiveRecommendedSingle');
export const markLiveRecommendationBooked =
  call<{ bookingId: string; offer: 'membership' | 'program'; membershipId?: string; orderId?: string },
    { status: 'booked' }>('markLiveRecommendationBooked');

// ── After the call (spec 10) ──────────────────────────────────────────────────

/** The Delivered card the server writes on completion. */
export interface LiveDeliveredCard {
  coachName: string;
  playerName: string;
  date: string;
  durationMinutes: number;
  /** url is null: the recording plays only through getLiveRecordingPlayback. */
  recording: { url: string | null; storagePath: string | null } | null;
  summary: {
    status: 'pending' | 'ready' | 'failed';
    whatWeCoveredHeading: string;
    whatWeCovered: string[];
    recommendsHeading: string;
    recommends: string[];
    recommendsSource: 'recommend' | 'transcript' | 'none';
  } | null;
  recommendation: { offer: RecommendOffer; title: string; priceCents: number; note: string; status: 'sent' | 'booked' } | null;
  offerSheetAgain: boolean;
  followUpPrefill: string;
}


/**
 * Decision 101: the coach saves the lesson's chapters (flags) on the booking,
 * before and during the call; the rules refuse anyone else, and after the finish.
 */
export function setLiveChapters(bookingId: string, chapters: LiveChapter[]): Promise<void> {
  return updateDoc(doc(db, 'liveCallBookings', bookingId), { liveChapters: chapters });
}

export const getLiveFollowUp =
  call<{ bookingId: string }, { conversationId: string; text: string }>('getLiveFollowUp');

// ── Membership (spec 4, 5) ────────────────────────────────────────────────────

export const refundLiveMembership =
  call<{ membershipId: string }, { status: string; refundCents?: number }>('refundLiveMembership');
/** The coach's new times for one lesson: one to three starts (ISO) on config/live's slot grid. */
export const proposeLiveLessonTimes =
  // holdId: a held date not yet booked (140), in place of bookingId.
  call<{ bookingId?: string; holdId?: string; startAts: string[] }, { proposalId: string }>('proposeLiveLessonTimes');
/** The player's answer: accept a time (moves the lesson), keep it, or open the picker. */
/**
 * Decision 123 (docs/booking-contracts.md 11): the player moves a lesson at
 * once to an open time (toSlotId, or toStartAt for the open time at that
 * start; with LIVE_DEV_ANY_TIME any future grid time). proposalId: the
 * coach's proposal answered with Pick something else.
 */
export const moveLiveLesson =
  call<{ bookingId: string; toSlotId?: string; toStartAt?: string; proposalId?: string; requestId?: string },
    { startAt: string }>('moveLiveLesson');
/** Decision 160: the side that suggested takes its times back (booking-contracts.md 15). */
export const withdrawLiveLessonTimeProposal =
  call<{ proposalId: string; requestId?: string }, { status: 'withdrawn'; bookingId: string | null }>('withdrawLiveLessonTimeProposal');
export const answerLiveLessonTimeProposal =
  call<{ proposalId: string; answer: 'accept' | 'keep' | 'pick'; time?: number; requestId?: string },
    { status: 'accepted' | 'kept' | 'picking'; startAt?: string }>('answerLiveLessonTimeProposal');
/** The coach takes back a weekly suggestion still open (165, contracts 16, Ryan's answers of 4 Nov). */
export const withdrawLiveWeeklyTimeProposal =
  call<{ proposalId: string }, { status: 'withdrawn'; membershipId: string }>('withdrawLiveWeeklyTimeProposal');
/** Looks Good on a move or a weekly change (decision 165, contracts 16): the record is answered; the move stands. */
export const acknowledgeLiveChange =
  call<{ changeId: string }, { changeId: string; state: string; answer: string }>('acknowledgeLiveChange');
/** Decision 96: the coach suggests one to three weekly slots from their week (their zone). */
export const proposeLiveWeeklyTime =
  call<{ membershipId: string; slots: Array<{ weekday: number; time: string }> }, { proposalId: string }>('proposeLiveWeeklyTime');
/** The player's answer to a suggestion: accept a slot, keep theirs, or open the picker. */
export const answerLiveWeeklyTimeProposal =
  call<{ proposalId: string; answer: 'accept' | 'keep' | 'pick'; slot?: number; lessonMode?: LessonModeKey },
    { status: 'accepted' | 'kept' | 'picking'; line?: string }>('answerLiveWeeklyTimeProposal');
/** Decision 84: a missed single booked again with the same coach, at no charge. */
export const rebookLiveMissedLesson =
  // startAt in place of slotId: DEV ONLY: LIVE_DEV_ANY_TIME.
  call<{ bookingId: string; slotId?: string; startAt?: string; lessonMode?: LessonModeKey; guidanceTopics?: string[] }, { bookingId: string }>('rebookLiveMissedLesson');
/** Decision 84: Refund instead. */
export const refundLiveMissedLesson =
  call<{ bookingId: string }, { status: 'refunded'; paymentStatus: string }>('refundLiveMissedLesson');
/** After the refund's cut-off: the player's refund request in their Double Bagel Support thread. */
export const requestLiveRefund =
  call<{ membershipId: string }, { threadId: string; requested: boolean }>('requestLiveRefund');
export const cancelLiveMembership =
  call<{ membershipId: string }, { status: string }>('cancelLiveMembership');
/** Undo a cancel while it is still ending: back to the status it had, renewing. */
export const undoCancelLiveMembership =
  call<{ membershipId: string }, { status: string }>('undoCancelLiveMembership');

// ── Earnings (spec 8) ─────────────────────────────────────────────────────────

/** A payout line, worded by the server ("Call with Ana, $112.50"). */
export interface LiveEarningsLine { text: string; at: string; cents: number }
export interface LiveEarnings {
  lines: LiveEarningsLine[];
  /** Holds, with an ISO release date or the server's own phrase. */
  pending: Array<LiveEarningsLine & { releaseAt: string }>;
}
export const getLiveEarnings = call<{ coachId?: string }, LiveEarnings>('getLiveEarnings');

// ── Admin (spec 9, MANUAL in v1; admin and root only) ─────────────────────────

/** One bucket of monitoring counts (spec 9); rates are misses over scheduled. */
export interface LiveCounts {
  scheduled: number; completed: number; playerMisses: number; coachMisses: number;
  missRate: number; playerMissRate: number; coachMissRate: number;
  noShows: number; lateJoins: number; lateJoinMinutes: number; lateCancels: number;
  cancelsWithNotice: number; timeRequests: number; moves: number; requestsUnanswered: number;
  adjustments: number; returned: number;
  paymentFailures: number; lapses: number;
}

/** Every event aggregated; months are "2026-12", in the player's zone. */
export interface LiveMonitoring {
  allTime: LiveCounts;
  byMonth: Record<string, LiveCounts>;
  byPlayer: Record<string, LiveCounts>;
  byCoach: Record<string, LiveCounts>;
  byPlayerMonth: Record<string, Record<string, LiveCounts>>;
}

/** One line of the alert feed, worded by the server. */
export interface LiveAlertEvent {
  id: string; type: string; at: string; text: string;
  coachId: string; playerId: string | null; bookingId: string | null; membershipId: string | null;
  lessonStartAt: string | null;
}

/** A member whose unused credits exceed the coach's open slots before their period ends. */
export interface LiveMemberBehind {
  membershipId: string; playerId: string; playerName: string | null;
  unusedCredits: number; openSlots: number; periodEnd: string;
}

export interface LiveCoachView {
  counts: LiveCounts;
  byMonth: Record<string, LiveCounts>;
  openSlots: number;
  membersUnusedCredits: number;
  members: number;
  cap: number | null;
  membersBehind: LiveMemberBehind[];
  events: Array<{ id: string; type: string; at: string; bookingId?: string; playerId?: string; data?: Record<string, unknown> }>;
}

export interface LiveMembershipAdminView {
  credits: {
    used: number; remaining: number; expired: number; label: string;
    flagged: { count: number; flaggedAt: string; expiresAt: string } | null;
    rolled: { count: number; fromPeriodStart: string; paidAt: string; expiresAt: string } | null;
    /** Admin Add credits: never paid to the coach. */
    fromDoubleBagel: { thisPeriod: number; unused: number; nextPeriod: number };
  };
  payouts: {
    adjustments: Array<{ id: string; amountCents: number; reason: string; status: string; createdAt: string; note?: string }>;
    bookings: Array<{ id: string; scheduledStartAt: string; status: string; paymentStatus?: string }>;
  };
  /** A period held in the refund window is two payouts: part "first" and "rest". */
  periodPayouts: Array<{ id: string; periodNumber?: number; part?: 'first' | 'rest'; coachCents: number; status: string; releaseAt?: string | null; periodStart?: string | null }>;
  billing: {
    status: string; lockedPriceCents: number; stripeSubscriptionId: string | null;
    periodAnchoredAt: string | null; anchorReason: string | null;
    cycleStartAt: string; cycleEndAt: string;
    billingPause: { reason: string; at: string } | null;
    paymentFailedAt: string | null; lapsedAt: string | null; refundedAt: string | null;
  };
}

export type LiveAdminAction =
  | 'restore_credit' | 'deduct_from_next_payout' | 'waive_deduction'
  | 'pause_billing' | 'resume_billing' | 'offer_transfer' | 'move_membership'
  | 'refund_remaining_credits' | 'add_credits';

export interface LiveAdminActionParams {
  action: LiveAdminAction;
  note: string;
  confirm: true;
  membershipId?: string;
  bookingId?: string;
  coachId?: string;
  amountCents?: number;
  adjustmentId?: string;
  coachIds?: string[];
  toCoachId?: string;
  /** add_credits: 1 to 8, to the current or the next period. */
  count?: number;
  period?: 'current' | 'next';
}

export const getLiveMonitoring =
  call<{ coachId?: string; playerId?: string }, LiveMonitoring>('getLiveMonitoring');
export const getLiveAlertFeed =
  call<{ limit?: number; before?: string }, { events: LiveAlertEvent[] }>('getLiveAlertFeed');
export const getLiveCoachView =
  call<{ coachId: string }, LiveCoachView>('getLiveCoachView');
export const getLiveMembershipAdminView =
  call<{ membershipId: string }, LiveMembershipAdminView>('getLiveMembershipAdminView');
export const liveLessonsAdminAction =
  call<LiveAdminActionParams, Record<string, unknown>>('liveLessonsAdminAction');

/**
 * Decision 142 (docs/booking-contracts.md 12): stops the lesson's ring on the other devices, once this
 * person has the lesson open here. Either of the pair may call it; nothing ringing is no error.
 */
export const stopRinging = call<{ bookingId: string }, { stopped: boolean }>('stopRinging');
